//* CS 응대 로그(답례품) 표·엑셀이 공유하는 파생값.
//* 설계 근거: docs/gift-cs-memo-log-improvement-checklist.md §2-B(D안) · §2-C
//*
//* 핵심 원칙 — **축이 달라도 역할이 같으면 한 열**.
//* 쿠폰 축과 배송 축은 쓰는 필드가 다르지만(fullCode/code, redeemStatus/deliveryStatus,
//* sentAt/shippedAt) 역할이 같다. 필드 기준으로 열을 만들면 행의 절반이 빈칸이 되므로,
//* 여기서 축별 필드를 역할 단위로 접어준다.

import { CouponRecipientInfo } from '@shared/api/giftCoupon/giftCoupon.controller';
import { GiftCsMemoLogItem } from '@shared/api/giftCsMemo/giftCsMemo.interface';
import { orderBrandName } from '@shared/utils/bugoBrandName.util';
import { CsMemoStatRow } from '@shared/utils/csMemoStats';

/** 메모가 달린 축 — 표의 `단위` 열 */
export type GiftCsMemoAxisKind = 'coupon' | 'delivery' | 'order';

export const axisOf = (item: GiftCsMemoLogItem): GiftCsMemoAxisKind => {
  if (item.giftCoupon) return 'coupon';
  if (item.giftDelivery) return 'delivery';
  return 'order';
};

/** 대상 식별 코드 — 쿠폰번호 또는 배송코드. 주문 축은 대상이 없다 */
export const targetCode = (item: GiftCsMemoLogItem): string => {
  if (item.coupon) {
    return (
      item.coupon.fullCode ??
      [item.coupon.code, item.coupon.seq].filter(Boolean).join('-')
    );
  }
  return item.delivery?.code ?? '';
};

/** 선택상품 — 쿠폰은 수령자가 고른 상품, 배송은 구매 시 고정된 상품 */
export const targetItemName = (item: GiftCsMemoLogItem): string =>
  item.coupon?.itemName ?? item.delivery?.itemName ?? '';

/**
 * 주문 유형 — `쿠폰형` / `배송형` / `혼합`.
 * 반드시 서버가 준 order.itemTypes 로만 판정한다. 메모 축으로 추론하면
 * 혼합 주문에서 틀린 값이 나오고, 그게 정확히 이 열을 만드는 이유인 케이스다.
 * 서버 필드가 아직 없으면 null → 화면이 유형 열 자체를 띄우지 않는다.
 */
export const orderTypeBadge = (
  item: GiftCsMemoLogItem,
): { label: string; className: string } | null => {
  const types = item.order?.itemTypes;
  if (!types || types.length === 0) return null;
  if (types.length > 1) {
    return { label: '혼합', className: 'bg-purple-100 text-purple-700' };
  }
  return types[0] === 'Coupon'
    ? { label: '쿠폰형', className: 'bg-blue-100 text-blue-700' }
    : { label: '배송형', className: 'bg-teal-100 text-teal-700' };
};

/**
 * 발송 일자 — 상태 배지 **바로 아래 보조 줄**로 쓴다.
 * `발송완료`와 `08.12`는 따로 읽으면 의미가 없어서 별도 열로 빼지 않는다.
 *
 * 축마다 일자의 뜻이 달라서 라벨을 같이 돌려준다:
 *  - 쿠폰: 관리자가 기프티콘을 **발송**한 시각(sentAt)
 *  - 배송: **도착**(deliveredAt) → 택배 **발송**(shippedAt) → **예정**(estimatedDeliveryDate) 순
 *
 * ⚠️ 쿠폰의 sentAt(발송)과 배송의 deliveredAt(도착)은 의미가 반대다. 두 행을 나란히 보는
 * 화면이라 라벨을 반드시 다르게 쓴다 (백엔드 가이드 §5-2).
 */
export const dispatchInfo = (
  item: GiftCsMemoLogItem,
): { label: string; date: Date } | null => {
  if (item.coupon?.sentAt) return { label: '발송', date: item.coupon.sentAt };
  if (item.delivery?.deliveredAt) {
    return { label: '도착', date: item.delivery.deliveredAt };
  }
  if (item.delivery?.shippedAt) return { label: '발송', date: item.delivery.shippedAt };
  if (item.delivery?.estimatedDeliveryDate) {
    return { label: '예정', date: item.delivery.estimatedDeliveryDate };
  }
  return null;
};

//* 발송 매체 — 매체마다 발신번호·알림 종류가 달라서 "모르는 번호로 문자 왔다" 응대에 쓴다
const DELIVERY_CHANNEL_LABEL: Record<string, string> = {
  Addcon: '애드콘',
  Officecon: '오피스콘',
  GiftiShow: '기프티쇼',
  EumGift: '1566-2373',
};

export const deliveryChannelLabel = (channel?: string): string =>
  channel ? DELIVERY_CHANNEL_LABEL[channel] ?? channel : '';

/**
 * 쿠폰 공유 진행 — "보냈는데 못 받았대요"의 실제 답은 발송(sentAt)이 아니라 여기 있다.
 * 공유는 됐는데 수령자가 교환신청을 안 했거나, 신청은 했는데 발송 대기 중인 경우를 가른다.
 */
export const shareProgress = (
  item: GiftCsMemoLogItem,
): { label: string; date?: Date } | null => {
  const status = item.coupon?.shareStatus;
  if (!status) return null;
  if (status === 'Viewed') return { label: '수령자 열람', date: item.coupon?.viewedAt };
  if (status === 'Shared') return { label: '공유함', date: item.coupon?.sharedAt };
  return { label: '미공유' };
};

/**
 * 실제 수령자 — **쿠폰·배송 두 축 공통**. 주문자(상주)와 다른 사람이다.
 *
 * 쿠폰은 구매자가 링크를 공유하고 **받은 사람이 자기 이름·번호로 교환신청**하므로,
 * 그 신청자가 곧 수령자다(coupon.recipient). 배송은 건마다 수령자가 따로 있다.
 * 두 축 다 "누가 받았냐"라는 같은 역할이라 한 자리에 접는다.
 *
 * 쿠폰은 미신청(NotShared/Shared)이면 아직 수령자가 없어 null —
 * 이때 화면에 빈 줄을 만들지 않도록 문자열이 아니라 객체/`null`로 돌려준다.
 *
 * 시각은 축마다 뜻이 달라(쿠폰=교환신청, 배송=배송지 제출) **라벨을 값과 같이** 돌려준다.
 * 일괄배송(Bulk)은 구매자가 주문 시 배송지를 넣으므로 배송 축은 비는 게 정상이다.
 */
export const recipientInfo = (
  item: GiftCsMemoLogItem,
): {
  /** 이름을 모르는 건(신청 전·재발행 쿠폰)은 빈 문자열 — 이때는 memo가 이름 자리다 */
  name: string;
  /** 전달자(구매자)가 남긴 호칭 메모 — 쿠폰 축 전용 */
  memo?: string;
  phoneNumber?: string;
  at?: { label: string; date: Date };
} | null => {
  if (
    item.coupon?.recipientName ||
    item.coupon?.recipientPhoneNumber ||
    item.coupon?.memo
  ) {
    return {
      name: item.coupon.recipientName ?? '',
      ...(item.coupon.memo ? { memo: item.coupon.memo } : {}),
      ...(item.coupon.recipientPhoneNumber
        ? { phoneNumber: item.coupon.recipientPhoneNumber }
        : {}),
      ...(item.coupon.requestedAt
        ? { at: { label: '신청', date: item.coupon.requestedAt } }
        : {}),
    };
  }
  if (item.delivery?.recipientName) {
    return {
      name: item.delivery.recipientName,
      ...(item.delivery.recipientPhoneNumber
        ? { phoneNumber: item.delivery.recipientPhoneNumber }
        : {}),
      ...(item.delivery.submittedAt
        ? { at: { label: '제출', date: item.delivery.submittedAt } }
        : {}),
    };
  }
  return null;
};

/**
 * 보강 조회 대상 쿠폰 id — **이 페이지의 쿠폰 행 전부**.
 *
 * 로그 요약이 아직 수령자·메모·인증·서명을 다 싣지 않아서(REQ-6/REQ-10), 부족한 값만
 * 골라 받으려 하면 "이 건은 원래 없는 값"과 "서버가 아직 안 주는 값"을 구분할 수 없다.
 * 페이지당 1요청(쿠폰 100건씩)이고 5분 캐시라 통째로 받는 편이 단순하고 정확하다.
 * 서버가 계약을 다 채우면 이 조회와 병합을 통째로 지운다.
 */
export const couponIdsForDetail = (items: GiftCsMemoLogItem[]): string[] =>
  items
    .filter((item) => item.coupon)
    .map((item) => item.coupon?._id ?? '')
    .filter(Boolean);

/**
 * 별도 조회한 쿠폰 수령자를 로그 행에 덮어씌운다 (표·엑셀 공용).
 * 서버 요약이 `coupon.recipientName` 을 내려주기 시작하면 대상 자체가 없어져
 * 이 함수도, 보강 조회도 조용히 무동작이 된다.
 */
export const withCouponRecipients = (
  items: GiftCsMemoLogItem[],
  recipients: Record<string, CouponRecipientInfo> | undefined,
): GiftCsMemoLogItem[] => {
  if (!recipients || Object.keys(recipients).length === 0) return items;
  return items.map((item) => {
    const coupon = item.coupon;
    if (!coupon) return item;
    const found = recipients[coupon._id];
    if (!found) return item;
    //* 서버 값이 항상 이긴다 — 보강분은 빈 자리만 채운다
    return {
      ...item,
      coupon: {
        ...coupon,
        recipientName: coupon.recipientName ?? found.name,
        recipientPhoneNumber: coupon.recipientPhoneNumber ?? found.phoneNumber,
        requestedAt: coupon.requestedAt ?? found.requestedAt,
        memo: coupon.memo ?? found.memo,
        verification: coupon.verification ?? found.verification,
        signatureImageUrl: coupon.signatureImageUrl ?? found.signatureImageUrl,
      },
    };
  });
};

/** 주문 식별용 짧은 번호 — 프로젝트 관례(ObjectId 뒤 6자리) */
export const shortOrderNo = (item: GiftCsMemoLogItem): string =>
  item.giftOrder ? item.giftOrder.slice(-6) : '';

/**
 * 응대 건수를 묶는 키 — **축 기준**.
 *
 * 주문 단위로 묶으면(예전 방식) 쿠폰 행에 "응대 3건"이 뜨는데 정작 `이력 보기`를 누르면
 * 그 쿠폰 메모 1건만 나온다. 배지는 **클릭했을 때 실제로 보이는 범위**와 같아야 한다.
 * 서로 다른 축이 섞이지 않도록 접두를 붙인다.
 */
export const axisKey = (item: GiftCsMemoLogItem): string | null => {
  if (item.giftCoupon) return `coupon:${item.giftCoupon}`;
  if (item.giftDelivery) return `delivery:${item.giftDelivery}`;
  if (item.giftOrder) return `order:${item.giftOrder}`;
  return null;
};

/** 서버가 준 축별 전체 건수 (없으면 undefined → 화면이 페이지 기준으로 근사) */
export const serverMemoCount = (item: GiftCsMemoLogItem): number | undefined => {
  if (item.giftCoupon) return item.coupon?.memoCount;
  if (item.giftDelivery) return item.delivery?.memoCount;
  return item.order?.memoCount;
};

/** 통계 패널이 아는 축으로 접기 (공용 집계기 입력) */
export const toCsMemoStatRows = (
  items: GiftCsMemoLogItem[],
  statusLabel: (item: GiftCsMemoLogItem) => string,
): CsMemoStatRow[] =>
  items.map((item) => ({
    createdAt: item.createdAt,
    byName: item.byName,
    ...(item.adminOnly ? { adminOnly: true } : {}),
    body: item.body,
    ...(axisKey(item) ? { axisKey: axisKey(item) as string } : {}),
    unit: item.giftCoupon ? '쿠폰' : item.giftDelivery ? '배송' : '주문',
    ...(orderTypeBadge(item) ? { orderType: orderTypeBadge(item)?.label } : {}),
    status: statusLabel(item),
    channel: deliveryChannelLabel(item.coupon?.deliveryChannel),
    itemName: targetItemName(item),
    ...(orderBrandName(item.order) ? { brand: orderBrandName(item.order) } : {}),
  }));
